<template>
    <div class="flex items-start gap-9">
        <BaseField
            :name="name"
            is-inline
            :label="fieldLabel"
        >
            <BaseSegmentedControl
                v-model="selectedKey"
                :name="name"
                :options="segmentedOptions"
                :is-disabled="isDisabled"
                :data-spec-class="timeRangeSpecClass"
            />
        </BaseField>
        <Transition
            enter-active-class="overflow-hidden transition-all duration-300 ease-in-out"
            enter-from-class="max-h-0 opacity-0"
            enter-to-class="max-h-24 opacity-100"
            leave-active-class="overflow-hidden transition-all duration-300 ease-in-out"
            leave-from-class="max-h-24 opacity-100"
            leave-to-class="max-h-0 opacity-0"
        >
            <div
                v-if="isCustom"
                class="flex items-start gap-3"
            >
                <div class="relative">
                    <BaseFieldInput
                        v-model="from"
                        type="datetime-local"
                        :name="fromName"
                        :data-spec-class="fromSpecClass"
                        is-inline
                        :is-disabled="isDisabled"
                        :label="t('general.from')"
                        :min="fromMin"
                        :max="fromMax"
                        :error-messages="fromErrorMessages"
                        @change="handleDateChange"
                    />
                    <div
                        v-if="contractStart || $slots.fromFooter"
                        class="flex items-center gap-1.5"
                    >
                        <span
                            class="invisible text-sm font-medium whitespace-nowrap"
                            aria-hidden="true"
                            >{{ t('general.from') }}</span
                        >
                        <div
                            v-if="contractStart"
                            :data-spec-class="contractStartSpecClass"
                            @click.prevent="applyContractStart"
                        >
                            <BaseLink custom-theme-base="text-xs hover:underline">
                                {{ t('general.contractStart') }}
                            </BaseLink>
                        </div>
                        <slot name="fromFooter" />
                    </div>
                </div>
                <BaseFieldInput
                    v-model="to"
                    type="datetime-local"
                    :name="toName"
                    :data-spec-class="toSpecClass"
                    is-inline
                    :is-disabled="isDisabled"
                    :label="t('general.to')"
                    :min="toMin"
                    :max="toMax"
                    :error-messages="toErrorMessages"
                    @change="handleDateChange"
                />
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { TimeRangeOption, TimeRangeSelection } from '@/types';
import { toDatetimeLocalString } from '@/utils/dateUtils';
import {
    CUSTOM_TIME_RANGE_KEY,
    isCustomTimeRange,
    timeRangeUnitMessageKey,
} from '@/utils/timeRangeUtils';
import BaseField from '@/components/BaseField.vue';
import BaseFieldInput from '@/components/BaseFieldInput.vue';
import BaseLink from '@/components/BaseLink.vue';
import BaseSegmentedControl from '@/components/BaseSegmentedControl.vue';

interface Props {
    options: TimeRangeOption[];
    hasCustomOption?: boolean;
    label?: string;
    name?: string;
    fromName?: string;
    toName?: string;
    isDisabled?: boolean;
    dataSpecPrefix?: string;
    fromMin?: string;
    toMax?: string;
    fromErrorMessages?: string[];
    toErrorMessages?: string[];
    contractStart?: string;
}

const props = withDefaults(defineProps<Props>(), {
    hasCustomOption: true,
    label: '',
    name: 'timeRange',
    fromName: 'from',
    toName: 'to',
    isDisabled: false,
    dataSpecPrefix: '',
    fromMin: undefined,
    toMax: undefined,
    fromErrorMessages: () => [],
    toErrorMessages: () => [],
    contractStart: undefined,
});

const emits = defineEmits<{
    change: [];
}>();

const { t } = useI18n();

const timeRange = defineModel<TimeRangeSelection>({ required: true });
const from = defineModel<string>('from', { default: '' });
const to = defineModel<string>('to', { default: '' });

const fieldLabel = computed(() => props.label || t('general.period'));

const availableOptions = computed<TimeRangeSelection[]>(() =>
    props.hasCustomOption ? [...props.options, CUSTOM_TIME_RANGE_KEY] : props.options
);

const selectionKey = (selection: TimeRangeSelection): string => {
    if (isCustomTimeRange(selection)) {
        return CUSTOM_TIME_RANGE_KEY;
    }

    return `${selection.value}_${selection.unit}`;
};

const segmentedOptions = computed(() =>
    availableOptions.value.map((option) => ({
        key: selectionKey(option),
        label: timeRangeLabel(option),
    }))
);

const timeRangeLabel = (option: TimeRangeSelection): string => {
    if (isCustomTimeRange(option)) {
        return t('general.custom');
    }

    return (
        option.label ||
        t(timeRangeUnitMessageKey(option.unit), { count: option.value }, option.value)
    );
};

const selectedKey = computed({
    get: () => selectionKey(timeRange.value),
    set: (key: string) => {
        const option = availableOptions.value.find((item) => selectionKey(item) === key);
        if (option) {
            timeRange.value = option;
        }
    },
});

const isCustom = computed(() => props.hasCustomOption && isCustomTimeRange(timeRange.value));

const timeRangeSpecClass = computed(() =>
    props.dataSpecPrefix ? `${props.dataSpecPrefix}__time-range` : 'time-range'
);
const fromSpecClass = computed(() =>
    props.dataSpecPrefix ? `${props.dataSpecPrefix}__from` : 'time-range__from'
);
const toSpecClass = computed(() =>
    props.dataSpecPrefix ? `${props.dataSpecPrefix}__to` : 'time-range__to'
);
const contractStartSpecClass = computed(() =>
    props.dataSpecPrefix ? `${props.dataSpecPrefix}__contract-start` : 'time-range__contract-start'
);

const fromMax = computed(() => to.value || toDatetimeLocalString(new Date()));
const toMin = computed(() => from.value || toDatetimeLocalString(new Date()));

const applyContractStart = () => {
    if (!props.contractStart) {
        return;
    }

    from.value = props.contractStart;
    handleDateChange();
};

const handleDateChange = () => {
    if (from.value && to.value) {
        const fromDate = new Date(from.value);
        const toDate = new Date(to.value);
        if (toDate < fromDate) {
            to.value = from.value;
        }
    }

    emits('change');
};
</script>
